'use client'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { type BridgePlatform, useInternalTransfer } from '@/hooks/useInternalTransfer'
import { CHAIN_CONFIG, getExplorerUrl } from '@/lib/api/relay'
import { useTrading } from '@/providers/TradingProvider'
import { AlertCircle, ArrowRight, CheckCircle2, ExternalLink, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

interface TransferModalProps {
	isOpen: boolean
	onClose: () => void
	mode?: 'manual' | 'trade-prompt'
	// For trade-prompt mode
	requiredAmount?: number
	targetPlatform?: 'polymarket' | 'kalshi'
	onSuccess?: () => void
}

export function TransferModal({
	isOpen,
	onClose,
	mode = 'manual',
	requiredAmount,
	targetPlatform,
	onSuccess,
}: TransferModalProps) {
	const { relayClient, isReady } = useTrading()

	const {
		polymarketBalance,
		kalshiBalance,
		quote,
		fetchQuote,
		execute,
		status,
		error,
		txHash,
		reset,
	} = useInternalTransfer()

	// Form state for manual mode
	const [amount, setAmount] = useState('')
	const [fromPlatform, setFromPlatform] = useState<BridgePlatform>('polymarket')
	const [toPlatform, setToPlatform] = useState<BridgePlatform>('kalshi')
	const [isMaxAmount, setIsMaxAmount] = useState(false)

	// Initialize for trade-prompt mode
	useEffect(() => {
		if (isOpen && mode === 'trade-prompt' && requiredAmount && targetPlatform) {
			const from: BridgePlatform = targetPlatform === 'polymarket' ? 'kalshi' : 'polymarket'
			const to: BridgePlatform = targetPlatform
			setFromPlatform(from)
			setToPlatform(to)
			setAmount(requiredAmount.toFixed(2))
			// Auto-fetch quote with EXACT_OUTPUT to ensure recipient gets the exact amount needed
			fetchQuote({
				amount: requiredAmount.toFixed(2),
				fromPlatform: from,
				toPlatform: to,
				exactOutput: true,
			})
		}
	}, [isOpen, mode, requiredAmount, targetPlatform, fetchQuote])

	// Reset when modal closes
	useEffect(() => {
		if (!isOpen) {
			setAmount('')
			setFromPlatform('polymarket')
			setToPlatform('kalshi')
			setIsMaxAmount(false)
			reset()
		}
	}, [isOpen, reset])

	const sourceBalance = fromPlatform === 'polymarket' ? polymarketBalance : kalshiBalance
	const sourceName = fromPlatform === 'polymarket' ? 'Polymarket' : 'Kalshi'
	const destName = toPlatform === 'kalshi' ? 'Kalshi' : 'Polymarket'

	const amountNum = Number.parseFloat(amount) || 0
	const isValidAmount = amountNum > 0 && (isMaxAmount || amountNum <= sourceBalance)

	const handleMax = useCallback(() => {
		setAmount(sourceBalance.toFixed(2))
		setIsMaxAmount(true)
	}, [sourceBalance])

	const handleGetQuote = useCallback(async () => {
		if (!isValidAmount) return
		await fetchQuote({
			amount,
			fromPlatform,
			toPlatform,
			useMaxBalance: isMaxAmount,
		})
	}, [amount, fromPlatform, toPlatform, isValidAmount, fetchQuote, isMaxAmount])

	const handleConfirm = useCallback(async () => {
		// Trading session auto-initializes on wallet connect
		if (fromPlatform === 'polymarket' && !isReady) {
			// Wait for session to be ready - should happen automatically
			return
		}
		await execute(relayClient)
	}, [execute, relayClient, fromPlatform, isReady])

	const handleClose = useCallback(() => {
		if (status === 'executing') return
		onClose()
	}, [status, onClose])

	const handleDone = useCallback(() => {
		if (status === 'success' && onSuccess) {
			onSuccess()
		}
		onClose()
	}, [status, onSuccess, onClose])

	const toggleDirection = useCallback(() => {
		setFromPlatform(f => (f === 'polymarket' ? 'kalshi' : 'polymarket'))
		setToPlatform(t => (t === 'kalshi' ? 'polymarket' : 'kalshi'))
		setAmount('')
		setIsMaxAmount(false)
		reset()
	}, [reset])

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
			<DialogContent className="gap-[24px] rounded-3xl border border-white/10 bg-brand-dark-light p-[24px] sm:max-w-[420px]">
				<DialogHeader>
					<DialogTitle className="text-center font-semibold text-[18px] text-white">
						{mode === 'trade-prompt' ? 'Transfer Required' : 'Transfer Funds'}
					</DialogTitle>
					<DialogDescription className="text-center text-brand-gray-secondary">
						{mode === 'trade-prompt'
							? `Move funds to ${destName} to complete your trade`
							: 'Move funds between your accounts'}
					</DialogDescription>
				</DialogHeader>

				{/* Form View */}
				{(status === 'idle' || status === 'loading_quote' || status === 'error') && (
					<div className="flex flex-col gap-4">
						{/* Balance Display with Direction */}
						<div className="rounded-xl border border-white/10 bg-brand-dark p-4">
							<div className="flex items-center justify-between">
								<div className="text-center">
									<div className="text-brand-gray-secondary text-xs">Polymarket</div>
									<div className="font-bold text-lg text-white">
										${polymarketBalance.toFixed(2)}
									</div>
								</div>

								{mode === 'manual' && (
									<button
										type="button"
										onClick={toggleDirection}
										className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-blue/20 text-brand-blue transition-colors hover:bg-brand-blue/30"
									>
										<ArrowRight
											className={`h-5 w-5 transition-transform ${toPlatform === 'polymarket' ? 'rotate-180' : ''}`}
										/>
									</button>
								)}

								{mode === 'trade-prompt' && (
									<ArrowRight
										className={`h-5 w-5 text-brand-blue ${toPlatform === 'polymarket' ? 'rotate-180' : ''}`}
									/>
								)}

								<div className="text-center">
									<div className="text-brand-gray-secondary text-xs">Kalshi</div>
									<div className="font-bold text-lg text-white">${kalshiBalance.toFixed(2)}</div>
								</div>
							</div>

							<div className="mt-3 text-center text-brand-gray-secondary text-sm">
								{sourceName} → {destName}
							</div>
						</div>

						{/* Amount Input */}
						<div className="space-y-2">
							<label htmlFor="transfer-amount" className="text-brand-gray-secondary text-sm">
								Amount (USDC)
							</label>
							<div className="relative">
								<input
									id="transfer-amount"
									type="number"
									value={amount}
									onChange={e => {
										setAmount(e.target.value)
										setIsMaxAmount(false)
									}}
									placeholder="0.00"
									min="0"
									step="0.01"
									disabled={mode === 'trade-prompt'}
									className="w-full rounded-xl border border-white/10 bg-brand-dark p-3 pr-16 font-mono text-white placeholder:text-brand-gray-secondary/50 focus:outline-none focus:ring-1 focus:ring-brand-blue disabled:opacity-50"
								/>
								{mode === 'manual' && (
									<button
										type="button"
										onClick={handleMax}
										className="-translate-y-1/2 absolute top-1/2 right-3 rounded-md bg-brand-blue/20 px-2 py-1 text-brand-blue text-xs hover:bg-brand-blue/30"
									>
										MAX
									</button>
								)}
							</div>
							{!isMaxAmount && amountNum > sourceBalance && (
								<p className="text-red-500 text-xs">Insufficient {sourceName} balance</p>
							)}
						</div>

						{/* Error */}
						{error && (
							<div className="flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-red-400 text-sm">
								<AlertCircle className="h-4 w-4 shrink-0" />
								{error}
							</div>
						)}

						{/* Submit Button */}
						<Button
							onClick={handleGetQuote}
							disabled={!isValidAmount || status === 'loading_quote'}
							className="h-12 w-full rounded-xl bg-brand-blue font-medium text-white hover:bg-brand-blue/90 disabled:opacity-50"
						>
							{status === 'loading_quote' ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Getting Quote...
								</>
							) : (
								'Continue'
							)}
						</Button>
					</div>
				)}

				{/* Quote Confirmation View */}
				{status === 'quote_ready' && quote && (
					<div className="flex flex-col gap-4">
						<div className="rounded-xl border border-white/10 bg-brand-dark p-4">
							<div className="flex items-center justify-between">
								<div className="text-center">
									<div className="text-brand-gray-secondary text-xs">{sourceName}</div>
									<div className="font-bold text-lg text-white">-${quote.inputAmount}</div>
								</div>
								<ArrowRight className="h-5 w-5 text-brand-blue" />
								<div className="text-center">
									<div className="text-brand-gray-secondary text-xs">{destName}</div>
									<div className="font-bold text-emerald-400 text-lg">
										+${quote.estimatedOutput}
									</div>
								</div>
							</div>
						</div>

						<div className="space-y-2 rounded-xl border border-white/10 bg-brand-dark p-4 text-sm">
							<div className="flex justify-between">
								<span className="text-brand-gray-secondary">Network Fee</span>
								<span className="text-emerald-400">Sponsored</span>
							</div>
							<div className="flex justify-between">
								<span className="text-brand-gray-secondary">Relay Fee</span>
								<span className="text-white">${quote.relayFeeUsd}</span>
							</div>
							{Number(quote.hlBridgeFeeUsd) > 0 && (
								<div className="flex justify-between">
									<span className="text-brand-gray-secondary">HL Bridge Fee</span>
									<span className="text-white">${quote.hlBridgeFeeUsd}</span>
								</div>
							)}
							<div className="flex justify-between">
								<span className="text-brand-gray-secondary">Est. Time</span>
								<span className="text-white">
									{quote.timeEstimate >= 60
										? `~${Math.round(quote.timeEstimate / 60)} min`
										: `~${quote.timeEstimate}s`}
								</span>
							</div>
						</div>

						<div className="flex gap-3">
							<Button
								variant="outline"
								onClick={reset}
								className="h-12 flex-1 rounded-xl border-white/10 text-white hover:bg-white/5"
							>
								Back
							</Button>
							<Button
								onClick={handleConfirm}
								className="h-12 flex-1 rounded-xl bg-brand-blue font-medium text-white hover:bg-brand-blue/90"
							>
								{mode === 'trade-prompt' ? 'Transfer & Continue' : 'Confirm Transfer'}
							</Button>
						</div>
					</div>
				)}

				{/* Executing View */}
				{status === 'executing' && (
					<div className="flex flex-col items-center gap-4 py-8">
						<Loader2 className="h-12 w-12 animate-spin text-brand-blue" />
						<div className="text-center">
							<p className="font-medium text-white">Processing Transfer</p>
							<p className="text-brand-gray-secondary text-sm">Moving funds to {destName}...</p>
						</div>
						<p className="text-center text-brand-gray-secondary text-xs">
							Please confirm any wallet prompts...
						</p>
					</div>
				)}

				{/* Success View */}
				{status === 'success' && quote && (
					<div className="flex flex-col items-center gap-4 py-8">
						<CheckCircle2 className="h-12 w-12 text-emerald-500" />
						<div className="text-center">
							<p className="font-medium text-white">Transfer Complete!</p>
							<p className="text-brand-gray-secondary text-sm">
								${quote.estimatedOutput} moved to {destName}
							</p>
						</div>
						{txHash && (
							<a
								href={getExplorerUrl(
									quote.fromPlatform === 'polymarket'
										? CHAIN_CONFIG.polygon.id
										: CHAIN_CONFIG.solana.id,
									txHash
								)}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-1 rounded-lg bg-white/5 px-3 py-1.5 text-blue-400 text-xs hover:bg-white/10"
							>
								View on Explorer <ExternalLink className="h-3 w-3" />
							</a>
						)}
						<Button
							onClick={handleDone}
							className="mt-2 h-12 w-full rounded-xl bg-brand-blue font-medium text-white hover:bg-brand-blue/90"
						>
							{mode === 'trade-prompt' ? 'Continue to Trade' : 'Done'}
						</Button>
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}
